import React, { useCallback } from 'react';
import { Checkbox, Grid } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { partial, times } from 'lodash';
import DragCanvas, { DragCanvasImage, DragCanvasText } from '@dyl/components/DragCanvas';
import ImagePicker from '@dyl/components/ImagePicker';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import { useImage } from '@dyl/systems/ImagesManager';
import PreviousImages from './utils/PreviousImages';
import {
    CheckCircle as CircleChecked,
    RadioButtonUnchecked as CircleUnchecked,
} from '@mui/icons-material';

const useStyles = makeStyles(theme => ({
    pickersContainer: {
        maxWidth: theme.breakpoints.values.md,
    },
}))

export default function PlacementEditor(
    {
        index,
        draggable = true,
        hasKeySelection = false,
        backgroundImagePath = 'background_image',
    }) {
    const classes = useStyles();

    const [backgroundImageId] = useTaskField(backgroundImagePath);
    const { src: backgroundImageSrc } = useImage(backgroundImageId);

    const [answers] = useTaskField('items', index, 'answers');

    return (
        <>
            <Grid container spacing={2} className={classes.pickersContainer}>
                {times(answers.length, answerIndex => (
                    <Grid item xs={12} sm={6} md={4} key={answerIndex}>
                        <AnswerImagePickers
                            index={answerIndex}
                            itemIndex={index}
                            hasKeySelection={hasKeySelection}
                        />
                    </Grid>
                ))}
            </Grid>

            <DragCanvas backgroundSrc={backgroundImageSrc}>
                <PreviousImages index={index} />
                {times(answers.length, answerIndex => (
                    <AnswerDraggable
                        key={answerIndex}
                        index={answerIndex}
                        itemIndex={index}
                        disabled={!draggable}
                    />
                ))}
            </DragCanvas>
        </>
    );
}

function AnswerImagePickers({ index, itemIndex, hasKeySelection = false }) {
    const useField = partial(useTaskField, 'items', itemIndex, 'answers', index);

    const [, setItems] = useTaskField('items', itemIndex, 'answers');
    const [promptId, setPromptId, promptError] = useField('image_id');
    const [placementId, setPlacementId, placementError] = useField('placement_image_id');


    const [isCorrect] = useField('is_correct_answer');
    const setAsCorrect = useCallback(() => {
        setItems(prev => prev.map((a, i) => ({
            ...a,
            is_correct_answer: i === index,
        })));
    }, [index, setItems]);

    const letterId = String.fromCharCode('A'.charCodeAt(0) + index);

    return (
        <>
            {hasKeySelection &&
                <Checkbox
                    checked={isCorrect}
                    onClick={setAsCorrect}
                    icon={<CircleUnchecked/>}
                    checkedIcon={<CircleChecked/>}
                    color='primary'
                />
            }
            <ImagePicker
                label={`Prompt Image ${letterId}`}
                value={promptId}
                onChange={setPromptId}
                fullWidth
                error={!!promptError}
                helperText={promptError ?? (hasKeySelection ? 'Select if key' : ' ')}
            />
            <ImagePicker
                value={placementId}
                onChange={id => setPlacementId(id)}
                label={`Placement Image ${letterId}`}
                helperText={placementError || ' '}
                error={!!placementError}
            />
        </>
    );
}

export function AnswerDraggable({ index, itemIndex, disabled = false, imageFieldName = 'placement_image_id' }) {
    const useField = partial(useTaskField, 'items', itemIndex, 'answers', index);

    const [imageId] = useField(imageFieldName);
    const { src } = useImage(imageId);

    const [coordinates, setCoordinates] = useField('coordinates');

    return (
        <DragCanvasImage
            disabled={disabled}
            src={src}
            alt={imageId}
            coordinates={coordinates}
            onCoordinatesChange={setCoordinates}
        />
    );
}

export function AnswerDraggableText({ index, itemIndex, textPath, disabled = false }) {
    const [text] = useTaskField(textPath);
    const useField = partial(useTaskField, 'items', itemIndex, 'answers', index);
    const [isCorrectAnswer] = useField('is_correct_answer');
    const [coordinates, setCoordinates] = useField('coordinates');

    return (
        <DragCanvasText
            disabled={disabled}
            text={text}
            coordinates={coordinates}
            onCoordinatesChange={setCoordinates}
            style = {{
                outlineColor: isCorrectAnswer ? 'green' : 'red',
                outlineStyle: 'dashed',
                outlineWidth: '4px',
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                borderRadius:'16px',
                width: '140px',
                height: '36px'
            }}
        />
    );
}